"use client"

import { useState } from "react"
import Link from "next/link"
import { useAuth, ApiError } from "@/lib/auth"

export default function TeacherLoginPage() {
  const { loginTeacher } = useAuth()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPw, setShowPw] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    const trimmed = email.trim()
    if (!trimmed) {
      setError("請輸入電子郵件")
      return
    }
    if (!password) {
      setError("請輸入密碼")
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const user = await loginTeacher(trimmed, password)
      const target = user.role === "ta" ? "/teacher/upload" : "/teacher/dashboard"
      window.location.assign(target)
      return
    } catch (err) {
      console.error("[teacher/login] failed", err)
      let msg: string
      if (err instanceof ApiError) {
        msg =
          err.status === 401
            ? "電子郵件或密碼錯誤，請再試一次"
            : err.message || "登入失敗，請稍後再試"
      } else if (err instanceof TypeError) {
        msg = "無法連線到伺服器，請檢查網路後再試"
      } else {
        msg = "登入失敗，請稍後再試"
      }
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{ background: "linear-gradient(135deg, #F0F8FF 0%, #E8F4FB 50%, #EDF7FF 100%)" }}
    >
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="text-center space-y-3">
          <div
            className="w-20 h-20 rounded-3xl mx-auto flex items-center justify-center text-4xl shadow-[0_8px_24px_rgba(77,184,232,0.3)]"
            style={{ background: "linear-gradient(135deg, #4DB8E8 0%, #2EA3D5 100%)" }}
          >
            🌏
          </div>
          <h1 className="text-2xl font-bold" style={{ color: "#2D3748" }}>語宙 YUZHOU</h1>
          <p className="text-sm" style={{ color: "#4DB8E8" }}>啟發每一次學習</p>
          <p className="text-sm" style={{ color: "#718096" }}>
            歡迎來到普通話學園，讓我們一起為孩子們創造充滿樂趣與探索感的學習體驗
          </p>
        </div>

        {/* 登入表單 */}
        <form
          onSubmit={onSubmit}
          noValidate
          className="rounded-3xl p-8 space-y-5"
          style={{ background: "white", boxShadow: "0 4px 24px rgba(77,184,232,0.12)" }}
        >
          <h2 className="font-bold text-lg" style={{ color: "#2D3748" }}>教師登入</h2>

          {error && (
            <p
              role="alert"
              className="text-sm rounded-xl px-4 py-3"
              style={{ background: "#FFE4E4", color: "#C53030" }}
            >
              {error}
            </p>
          )}

          {/* 電子郵件 */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium" style={{ color: "#2D3748" }}>電子郵件</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg">✉️</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="teacher@school.edu.hk"
                className="w-full rounded-2xl border-2 pl-11 pr-5 py-3 outline-none transition-all"
                style={{
                  borderColor: email ? "#4DB8E8" : "#E2E8F0",
                  background: "#FAFBFF",
                  color: "#2D3748",
                }}
              />
            </div>
          </div>

          {/* 密碼 */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium" style={{ color: "#2D3748" }}>密碼</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg">🔒</span>
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="輸入密碼"
                className="w-full rounded-2xl border-2 pl-11 pr-12 py-3 outline-none transition-all"
                style={{
                  borderColor: password ? "#4DB8E8" : "#E2E8F0",
                  background: "#FAFBFF",
                  color: "#2D3748",
                }}
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-lg"
                style={{ color: "#718096" }}
              >
                {showPw ? "🙈" : "👁"}
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <button type="button" className="text-sm cursor-pointer" style={{ color: "#4DB8E8" }}>
              忘記密碼？
            </button>
          </div>

          {/* 登入按鈕 */}
          <button
            type="submit"
            disabled={submitting}
            className="flex items-center justify-center gap-2 w-full rounded-full py-4 text-white font-bold transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
            style={{
              background: "linear-gradient(135deg, #4DB8E8 0%, #2EA3D5 100%)",
              boxShadow: "0 4px 16px rgba(77,184,232,0.35)",
            }}
          >
            {submitting ? "登入中..." : "登入 →"}
          </button>

          <p className="text-center text-xs" style={{ color: "#718096" }}>
            遇到問題？
            <button type="button" className="ml-1 font-medium" style={{ color: "#4DB8E8" }}>
              聯繫技術支援
            </button>
          </p>
        </form>

        <div className="text-center">
          <Link href="/" className="text-sm" style={{ color: "#718096" }}>← 返回首頁</Link>
        </div>
      </div>
    </div>
  )
}
